import { useState, useEffect } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, getDoc, updateDoc, setDoc, increment } from "firebase/firestore";

// 🏟️ Import Team Logos from src/logos
import CSK from "../logos/CSK.png";
import DC from "../logos/DC.png";
import GT from "../logos/GT.png";
import KKR from "../logos/KKR.png";
import LSG from "../logos/LSG.png";
import MI from "../logos/MI.png";
import PBKS from "../logos/PBKS.png";
import RCB from "../logos/RCB.png";
import RR from "../logos/RR.png";
import SRH from "../logos/SRH.png";

const teamLogos = { CSK, DC, GT, KKR, LSG, MI, PBKS, RCB, RR, SRH };

const teamColors = {
  CSK: "#fdb913",
  DC: "#283482",
  GT: "#1b2133",
  KKR: "#3a225d",
  LSG: "#91263d",
  MI: "#004ba0",
  PBKS: "#ed1b24",
  RR: "#ea1b8d",
  RCB: "#d11d26",
  SRH: "#f26522"
};

function MatchCard({ id, teamA, teamB, venue, date, time, status, winner, predictWindow, teamAScore, teamBScore, resultReason }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const checkLockStatus = () => {
      try {
        const [year, month, day] = date.split('-').map(Number);
        const [hours, minutes] = time.split(':').map(Number);
        const matchStart = new Date(year, month - 1, day, hours, minutes);

        // Use custom predictWindow if provided, otherwise default to 20 minutes
        const windowMinutes = predictWindow !== undefined ? predictWindow : 20;
        const lockTime = new Date(matchStart.getTime() + windowMinutes * 60 * 1000);
        const now = new Date();

        setIsLive(now >= matchStart && status !== 'finished' && status !== 'cancelled');

        if (now > lockTime) {
          setIsLocked(true);
          setTimeRemaining("Predictions Locked 🔒");
        } else {
          setIsLocked(false);
          const diff = matchStart.getTime() - now.getTime();
          if (diff > 0) {
            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            setTimeRemaining(`Locks in ${h}h ${m}m`);
          } else {
            const remainingLive = lockTime.getTime() - now.getTime();
            const liveMin = Math.floor(remainingLive / (1000 * 60));
            setTimeRemaining(`Live Window: ${liveMin}m left`);
          }
        }
      } catch (err) {
        console.error("Date parsing failed", err);
      }
    };

    checkLockStatus();
    const timer = setInterval(checkLockStatus, 60000);
    return () => clearInterval(timer);
  }, [date, time, predictWindow, status]);

  useEffect(() => {
    const fetchPrediction = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const predictions = userSnap.data().predictions || [];
          const myPick = predictions.find(p => p.matchId.toString() === id.toString());
          if (myPick) setPrediction(myPick.team);
        }
      }
    };
    fetchPrediction();
  }, [id, status]);

  const handlePrediction = async (team) => {
    const user = auth.currentUser;
    if (!user || loading || status === 'finished' || isLocked) return;
    if (prediction && !isEditing) return;

    setLoading(true);
    try {
      const userRef = doc(db, "users", user.uid);
      const predictionData = {
        matchId: parseInt(id),
        team: team,
        timestamp: new Date().toISOString()
      };

      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const existing = userSnap.data().predictions || [];
        // Remove old prediction for this match if it exists
        const newPredictions = existing.filter(p => p.matchId.toString() !== id.toString());
        newPredictions.push(predictionData);

        const updates = { predictions: newPredictions };
        // Only increment total if this is a brand new prediction
        if (existing.length === newPredictions.length - 1) {
          updates.totalPredictions = increment(1);
        }
        await updateDoc(userRef, updates);
      } else {
        await updateDoc(userRef, {
          predictions: [predictionData],
          totalPredictions: increment(1)
        });
      }

      const matchPredictionRef = doc(db, "match_predictions", `${id}_${user.uid}`);
      await setDoc(matchPredictionRef, {
        ...predictionData,
        userId: user.uid,
        userName: user.displayName || user.email.split('@')[0]
      });

      setPrediction(team);
      setIsEditing(false);
    } catch (err) {
      console.error("Prediction failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const TeamLogo = ({ team }) => (
    <div style={{ ...styles.logoContainer, background: teamColors[team] || 'rgba(255,255,255,0.05)' }}>
      <img src={teamLogos[team]} alt={team} style={styles.actualLogo} />
    </div>
  );

  const isWinner = (team) => winner === team;

  return (
    <div className="glass-morphism animate-fade-in" style={{ ...styles.card, opacity: ((status === 'finished' || status === 'cancelled') || (isLocked && !prediction)) ? 0.9 : 1, borderTop: (status === 'finished' || status === 'cancelled') ? '4px solid #ef4444' : '1px solid var(--glass-border)' }}>
      <div style={styles.header}>
        <span style={styles.venue}>{venue}</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <span style={styles.timerBadge}>{timeRemaining}</span>
          <span style={{
            ...styles.statusBadge,
            background: (status === 'finished' || status === 'cancelled') ? 'rgba(239, 68, 68, 0.1)' : isLive ? 'rgba(234, 179, 8, 0.1)' : 'rgba(16, 185, 129, 0.1)',
            color: (status === 'finished' || status === 'cancelled') ? '#ef4444' : isLive ? '#eab308' : '#10b981'
          }}>
            {status === 'finished' ? 'FINISHED' : status === 'cancelled' ? 'CANCELLED' : isLive ? 'IN PROGRESS' : (status?.toUpperCase() || 'UPCOMING')}
          </span>
        </div>
      </div>

      <div style={styles.matchMain}>
        <div style={styles.teamSection}>
          <TeamLogo team={teamA} />
          <h3 style={{ ...styles.teamName, color: isWinner(teamA) ? 'var(--primary)' : 'var(--text-primary)' }}>
            {teamA} {isWinner(teamA) && "🏆"}
          </h3>
        </div>

        <div style={styles.vsSection}>
          <span style={styles.vs}>VS</span>
          <div style={styles.dateTime}>
            <div style={{ fontSize: '0.75rem', opacity: 0.6, color: 'var(--text-secondary)' }}>{date}</div>
            <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--primary)' }}>{time}</div>
          </div>
        </div>

        <div style={styles.teamSection}>
          <TeamLogo team={teamB} />
          <h3 style={{ ...styles.teamName, color: isWinner(teamB) ? 'var(--primary)' : 'var(--text-primary)' }}>
            {teamB} {isWinner(teamB) && "🏆"}
          </h3>
        </div>
      </div>

      <div style={styles.actionSection}>
        {status === 'finished' ? (
          <div style={styles.finishedCard}>
            <p style={{ fontWeight: '800', letterSpacing: '0.05em', color: 'var(--primary)', marginBottom: (teamAScore || teamBScore) ? '0.5rem' : '0' }}>
              RESULT: {resultReason ? resultReason.toUpperCase() : `${winner?.toUpperCase()} WON`}
            </p>
            {(teamAScore || teamBScore) && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', fontSize: '0.9rem', fontWeight: '800', background: 'rgba(255,255,255,0.05)', padding: '0.5rem', borderRadius: '8px', marginBottom: '0.5rem' }}>
                {teamAScore && <span>{teamA}: {teamAScore}</span>}
                {teamAScore && teamBScore && <span style={{ opacity: 0.5 }}>|</span>}
                {teamBScore && <span>{teamB}: {teamBScore}</span>}
              </div>
            )}
            {prediction && (
              <div style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <p style={{ fontSize: '0.75rem', opacity: 0.6, letterSpacing: '0.1em', marginBottom: '0.2rem' }}>MY PICK: {prediction}</p>
                <p style={{ fontSize: '0.9rem', color: prediction === winner ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                  {prediction === winner ? "🎯 HIT! REWARD AWARDED" : `❌ MISS. 0 POINTS.`}
                </p>
              </div>
            )}
          </div>
        ) : status === 'cancelled' ? (
          <div style={styles.finishedCard}>
            <p style={{ fontWeight: '800', letterSpacing: '0.05em', color: '#f59e0b' }}>MATCH ABANDONED</p>
            {prediction && (
              <p style={{ fontSize: '0.9rem', color: '#10b981', fontWeight: 'bold', marginTop: '0.2rem' }}>
                VOID REFUND: +10 PTS
              </p>
            )}
          </div>
        ) : !prediction || isEditing ? (
          <>
            {isLocked ? (
              <div style={styles.lockedNote}>
                🔒 TIME'S UP! PREDICTIONS LOCKED.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <p style={{ ...styles.prompt, marginBottom: 0 }}>MAKE YOUR PICK</p>
                  {isEditing && <button onClick={() => setIsEditing(false)} style={styles.cancelBtn} disabled={loading}>(CANCEL)</button>}
                </div>
                <div style={styles.buttonGroup}>
                  <button style={styles.predictBtn} onClick={() => handlePrediction(teamA)} disabled={loading}>
                    SELECT {teamA}
                  </button>
                  <button style={styles.predictBtn} onClick={() => handlePrediction(teamB)} disabled={loading}>
                    SELECT {teamB}
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <div style={styles.resultDisplay}>
            <div style={{ fontSize: '0.75rem', letterSpacing: '0.1em', opacity: 0.6 }}>MY PREDICTION</div>
            <div style={styles.myPickName}>{prediction}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={styles.pickStatusBadge}>{isLocked ? "LOCK-IN STAGE 🔒" : "WAITING FOR RESULT..."}</div>
              {!isLocked && (
                <button onClick={() => setIsEditing(true)} style={styles.editBtn}>✏️ EDIT</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: { padding: "1.5rem", borderRadius: "24px", display: "flex", flexDirection: "column", gap: "1.2rem", transition: '0.3s' },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  venue: { fontSize: "0.65rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: '700', letterSpacing: '0.1em' },
  statusBadge: { fontSize: "0.6rem", padding: "0.25rem 0.75rem", borderRadius: "99px", fontWeight: "900", letterSpacing: '0.05em' },
  timerBadge: { fontSize: "0.6rem", color: "var(--primary)", background: "rgba(0,118,255,0.08)", padding: "0.25rem 0.75rem", borderRadius: "99px", fontWeight: "800" },
  matchMain: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: '0.5rem 0' },
  teamSection: { display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem", flex: 1 },
  teamName: { fontSize: "1.2rem", fontWeight: "900", textAlign: 'center' },
  logoContainer: { width: "75px", height: "75px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", overflow: 'hidden', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' },
  actualLogo: { width: "90%", height: "90%", objectFit: "contain" },
  vsSection: { display: "flex", flexDirection: "column", alignItems: "center", gap: "0.4rem" },
  vs: { fontSize: "0.9rem", fontWeight: "900", color: "var(--primary)", opacity: 0.8 },
  dateTime: { textAlign: "center", background: 'rgba(255,255,255,0.02)', padding: '0.4rem 0.8rem', borderRadius: '12px' },
  actionSection: { borderTop: "1px solid var(--glass-border)", paddingTop: "1.2rem", textAlign: "center", display: 'flex', flexDirection: 'column', gap: '1rem' },
  prompt: { fontSize: "0.75rem", color: "var(--text-secondary)", marginBottom: "0.5rem", fontWeight: '800', letterSpacing: '0.05em' },
  buttonGroup: { display: "flex", gap: "0.8rem", justifyContent: "center" },
  predictBtn: { flex: 1, padding: "0.75rem", border: "1px solid var(--glass-border)", background: "rgba(255,255,255,0.01)", color: "var(--text-primary)", borderRadius: "12px", fontWeight: "800", fontSize: '0.8rem', transition: '0.2s', cursor: 'pointer' },
  resultDisplay: { display: "flex", flexDirection: "column", alignItems: 'center' },
  myPickName: { fontSize: '1.4rem', fontWeight: '900', color: 'var(--primary)', margin: '0.2rem 0' },
  pickStatusBadge: { fontSize: '0.65rem', background: 'rgba(59,130,246,0.1)', color: 'var(--primary)', padding: '0.3rem 1rem', borderRadius: '50px', fontWeight: '800' },
  editBtn: { fontSize: '0.65rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)', padding: '0.3rem 0.8rem', borderRadius: '50px', fontWeight: '800', border: '1px solid var(--glass-border)', cursor: 'pointer', transition: '0.2s' },
  cancelBtn: { fontSize: '0.6rem', background: 'none', border: 'none', color: '#ef4444', fontWeight: '800', cursor: 'pointer', textDecoration: 'underline' },
  finishedCard: { background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '16px', border: '1px solid var(--glass-border)' },
  lockedNote: { fontSize: '0.75rem', color: '#64748b', background: 'rgba(255,255,255,0.01)', padding: '0.6rem', borderRadius: '10px', fontWeight: '900' }
};

export default MatchCard;
